/**
 * SwarmWorkspace — a ChatGPT-style left sidebar for a swarm agent.
 * Shows: "New task", the swarm's Projects (each expandable to its tasks, with
 * per-project document upload → always-used grounding), and the full task
 * history (every question asked) with rename / delete / move-to-project.
 *
 * Props:
 *   swarm, theme (T: {bg,panel,border,textH,textB,textDim,accent}), brand,
 *   historyApi (e.g. `${API}/csh/swarm`), activeRunId, refreshKey,
 *   onLoadTask(run_id), onNewTask()
 */
import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, FolderKanban, ChevronRight, ChevronDown, MessageSquarePlus, Trash2, Pencil,
  UploadCloud, Loader2, FolderInput, Check, X, PanelLeftClose, PanelLeftOpen, FileText, ListChecks, Search,
  Users, Lock, Trophy,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SwarmWorkspace({ swarm, theme: T, brand, historyApi, activeRunId, refreshKey, onLoadTask, onNewTask, onOpenProject }) {
  const [collapsed, setCollapsed] = useState(false);
  const [projects, setProjects] = useState([]);
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCat, setNewCat] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [uploadingTo, setUploadingTo] = useState(null);
  const [moveFor, setMoveFor] = useState(null);
  const [search, setSearch] = useState("");
  const [shareNew, setShareNew] = useState(false);
  const [showBoard, setShowBoard] = useState(false);
  const [board, setBoard] = useState(null);

  const loadProjects = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/projects?swarm=${encodeURIComponent(swarm)}`); setProjects(data.projects || []); }
    catch (_) { setProjects([]); }
  }, [swarm]);

  const loadHistory = useCallback(async () => {
    try { const { data } = await axios.get(`${historyApi}/history`); setHistory(Array.isArray(data) ? data : (data.tasks || data.history || data.runs || [])); }
    catch (_) { setHistory([]); }
  }, [historyApi]);

  useEffect(() => { loadProjects(); loadHistory(); }, [loadProjects, loadHistory, refreshKey]);

  const toggleProject = async (id) => {
    if (expanded[id]) { setExpanded((e) => ({ ...e, [id]: undefined })); return; }
    try { const { data } = await axios.get(`${API}/projects/${id}`); setExpanded((e) => ({ ...e, [id]: data })); }
    catch (_) { toast.error("Could not open project"); }
  };
  const refreshProject = async (id) => {
    try { const { data } = await axios.get(`${API}/projects/${id}`); setExpanded((e) => ({ ...e, [id]: data })); } catch (_) {}
    loadProjects();
  };

  const createProject = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/projects`, { swarm, name: newName.trim(), category: newCat.trim(), shared: shareNew });
      toast.success("Project created");
      setNewName(""); setNewCat(""); setShareNew(false); setCreating(false); loadProjects();
    } catch (_) { toast.error("Could not create project"); }
    setSaving(false);
  };

  const openBoard = async () => {
    setShowBoard(true); setBoard(null);
    try { const { data } = await axios.get(`${API}/projects/leaderboard/${encodeURIComponent(swarm)}`); setBoard(data.leaderboard || []); }
    catch (_) { setBoard([]); }
  };
  const fmtUsd = (v) => {
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
    if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  };

  const deleteProject = async (id, name) => {
    if (!window.confirm(`Delete project "${name}"? Tasks stay in history; only the grouping is removed.`)) return;
    try { await axios.delete(`${API}/projects/${id}`); setExpanded((e) => ({ ...e, [id]: undefined })); loadProjects(); }
    catch (_) { toast.error("Could not delete project"); }
  };

  const uploadDoc = async (projectId, file) => {
    if (!file) return;
    setUploadingTo(projectId);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("title", file.name);
      await axios.post(`${API}/projects/${projectId}/knowledge/upload`, fd);
      toast.success("Uploaded — ingesting into grounding");
      setTimeout(() => refreshProject(projectId), 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setUploadingTo(null);
  };

  const renameTask = async (run_id) => {
    try { await axios.patch(`${historyApi}/runs/${run_id}`, { label: draft.trim() || null }); setEditing(null); loadHistory(); }
    catch (_) { toast.error("Could not rename"); }
  };
  const deleteTask = async (run_id) => {
    try { await axios.delete(`${historyApi}/runs/${run_id}`); loadHistory(); if (run_id === activeRunId) onNewTask?.(); }
    catch (_) { toast.error("Could not delete task"); }
  };
  const moveToProject = async (run_id, title, projectId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/tasks`, { swarm, run_id, task_title: title || "" });
      toast.success("Added to project"); setMoveFor(null);
      if (expanded[projectId]) refreshProject(projectId); else loadProjects();
    } catch (_) { toast.error("Could not add to project"); }
  };

  const S = {
    side: { width: collapsed ? 52 : 288, flex: "0 0 auto", borderRight: `1px solid ${T.border}`, background: T.panel,
      height: "100vh", position: "sticky", top: 0, display: "flex", flexDirection: "column", transition: "width 0.18s ease", overflow: "hidden" },
    section: { padding: "6px 12px", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: T.textDim, fontWeight: 700 },
    item: { display: "flex", alignItems: "center", gap: 8, padding: "7px 9px", borderRadius: 8, cursor: "pointer", fontSize: 13, color: T.textB },
    input: { background: T.bg, border: `1px solid ${T.border}`, borderRadius: 7, padding: "6px 8px", fontSize: 12.5, color: T.textH, outline: "none", width: "100%" },
    iconBtn: { background: "none", border: "none", cursor: "pointer", color: T.textDim, display: "flex", padding: 2, alignItems: "center" },
  };

  if (collapsed) {
    return (
      <div style={S.side} data-testid="swarm-workspace-collapsed">
        <button onClick={() => setCollapsed(false)} style={{ ...S.iconBtn, margin: "12px auto", color: T.accent }} title="Open workspace" data-testid="workspace-expand"><PanelLeftOpen size={20} /></button>
        <button onClick={onNewTask} style={{ ...S.iconBtn, margin: "4px auto", color: T.textB }} title="New task"><MessageSquarePlus size={20} /></button>
      </div>
    );
  }

  return (
    <div style={S.side} data-testid="swarm-workspace">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 12px 8px" }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: T.textH, letterSpacing: "0.04em" }}>{brand} · Workspace</span>
        <button onClick={() => setCollapsed(true)} style={S.iconBtn} title="Collapse" data-testid="workspace-collapse"><PanelLeftClose size={16} /></button>
      </div>

      <div style={{ padding: "0 10px 8px" }}>
        <button onClick={onNewTask} data-testid="workspace-new-task"
          style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, justifyContent: "center", padding: "9px 12px",
            borderRadius: 9, border: `1px solid ${T.accent}55`, background: `${T.accent}12`, color: T.accent, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          <MessageSquarePlus size={15} /> New task
        </button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", paddingBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", ...S.section }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><FolderKanban size={12} /> Projects</span>
          <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
            <button onClick={openBoard} style={S.iconBtn} title="Value leaderboard" data-testid="workspace-leaderboard-btn"><Trophy size={14} /></button>
            <button onClick={() => setCreating((v) => !v)} style={S.iconBtn} title="New project" data-testid="workspace-new-project"><Plus size={15} /></button>
          </div>
        </div>

        {creating && (
          <div style={{ padding: "4px 10px 10px", display: "flex", flexDirection: "column", gap: 6 }} data-testid="workspace-create-project">
            <input value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus placeholder="Project name"
              onKeyDown={(e) => { if (e.key === "Enter") createProject(); if (e.key === "Escape") setCreating(false); }} style={S.input} data-testid="workspace-project-name" />
            <input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Type / category (optional)"
              onKeyDown={(e) => { if (e.key === "Enter") createProject(); }} style={S.input} data-testid="workspace-project-category" />
            <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: T.textB, cursor: "pointer", padding: "1px 2px" }} data-testid="workspace-project-share">
              <input type="checkbox" checked={shareNew} onChange={(e) => setShareNew(e.target.checked)} style={{ accentColor: T.accent }} />
              {shareNew ? <Users size={13} color={T.accent} /> : <Lock size={13} color={T.textDim} />}
              Share with my team
            </label>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <button onClick={() => setCreating(false)} style={{ ...S.iconBtn, fontSize: 12, color: T.textDim }}>Cancel</button>
              <button onClick={createProject} disabled={saving || !newName.trim()} data-testid="workspace-project-create"
                style={{ display: "flex", alignItems: "center", gap: 5, background: T.accent, border: "none", borderRadius: 7, padding: "5px 10px", color: "#04211A", fontSize: 12.5, fontWeight: 600, cursor: "pointer", opacity: saving || !newName.trim() ? 0.5 : 1 }}>
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Create
              </button>
            </div>
          </div>
        )}

        {projects.length === 0 && !creating && (<div style={{ padding: "2px 12px 8px", fontSize: 12, color: T.textDim }}>No projects yet.</div>)}

        {projects.map((p) => {
          const det = expanded[p.id];
          const open = !!det;
          return (
            <div key={p.id} style={{ padding: "0 6px" }}>
              <div style={{ ...S.item, justifyContent: "space-between" }} className="ws-row" data-testid={`workspace-project-${p.id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 }}>
                  <button onClick={(e) => { e.stopPropagation(); toggleProject(p.id); }} style={S.iconBtn} data-testid={`workspace-project-expand-${p.id}`}>
                    {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  <span onClick={() => (onOpenProject ? onOpenProject(p.id) : toggleProject(p.id))} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{p.name}</span>
                  {p.shared
                    ? <span title={p.is_owner ? "Shared with your team" : `Shared by ${p.created_by_name || "a teammate"}`} data-testid={`workspace-project-shared-${p.id}`}><Users size={12} color={T.accent} /></span>
                    : <span title="Private to you"><Lock size={11} color={T.textDim} /></span>}
                  {p.category ? <span style={{ fontSize: 9.5, padding: "1px 5px", borderRadius: 6, background: T.bg, border: `1px solid ${T.border}`, color: T.textDim }}>{p.category}</span> : null}
                </div>
                <div className="ws-actions" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 10, color: T.textDim }}>{p.task_count}</span>
                  <label style={S.iconBtn} title="Upload document" data-testid={`workspace-project-upload-${p.id}`}>
                    {uploadingTo === p.id ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />}
                    <input type="file" hidden accept=".pdf,.docx,.txt,.md,.csv,.html,.pptx" onChange={(e) => { if (e.target.files[0]) uploadDoc(p.id, e.target.files[0]); e.target.value = ""; }} />
                  </label>
                  {p.is_owner && (
                    <button onClick={() => deleteProject(p.id, p.name)} style={S.iconBtn} title="Delete project" data-testid={`workspace-project-delete-${p.id}`}><Trash2 size={13} /></button>
                  )}
                </div>
              </div>

              {open && (
                <div style={{ paddingLeft: 20, paddingBottom: 4 }}>
                  {(det.knowledge_sources || []).length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 4 }}>
                      {det.knowledge_sources.map((s) => (
                        <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: T.textDim, padding: "3px 6px" }}>
                          <FileText size={11} /> <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{s.name}</span>
                          <span style={{ fontSize: 9, color: s.status === "ready" ? T.accent : T.textDim }}>{s.status === "ready" ? "✓" : "…"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(det.tasks || []).length === 0 ? (
                    <div style={{ fontSize: 11.5, color: T.textDim, padding: "2px 6px" }}>No tasks. Move a question here.</div>
                  ) : det.tasks.map((t) => (
                    <div key={t.id} onClick={() => onLoadTask(t.run_id)} data-testid={`workspace-ptask-${t.id}`}
                      style={{ ...S.item, padding: "5px 6px", fontSize: 12.5, background: activeRunId === t.run_id ? `${T.accent}18` : "transparent" }}>
                      <MessageSquarePlus size={11} style={{ opacity: 0.6 }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.task_title || t.run_id}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div style={{ ...S.section, marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}><ListChecks size={12} /> Tasks</div>
        <div style={{ padding: "0 10px 6px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "5px 8px" }}>
            <Search size={13} color={T.textDim} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tasks…" data-testid="workspace-search"
              style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: T.textH, fontSize: 12.5 }} />
            {search && <button onClick={() => setSearch("")} style={S.iconBtn}><X size={12} /></button>}
          </div>
        </div>
        {(() => {
          const q = search.trim().toLowerCase();
          const filtered = q ? history.filter((t) => `${t.label || ""} ${t.query || ""} ${t.prompt || ""} ${t.run_id}`.toLowerCase().includes(q)) : history;
          if (history.length === 0) return <div style={{ padding: "2px 12px", fontSize: 12, color: T.textDim }}>No tasks yet. Ask a question to start.</div>;
          if (filtered.length === 0) return <div style={{ padding: "2px 12px", fontSize: 12, color: T.textDim }} data-testid="workspace-search-empty">No tasks match "{search}".</div>;
          return (
        <div style={{ padding: "0 6px" }}>
          {filtered.map((t) => (
            <div key={t.run_id} className="ws-row" style={{ position: "relative" }}>
              {editing === t.run_id ? (
                <div style={{ display: "flex", gap: 4, padding: "4px 6px" }}>
                  <input value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus style={S.input}
                    onKeyDown={(e) => { if (e.key === "Enter") renameTask(t.run_id); if (e.key === "Escape") setEditing(null); }} data-testid={`workspace-rename-input-${t.run_id}`} />
                  <button onClick={() => renameTask(t.run_id)} style={S.iconBtn}><Check size={14} color={T.accent} /></button>
                  <button onClick={() => setEditing(null)} style={S.iconBtn}><X size={14} /></button>
                </div>
              ) : (
                <div style={{ ...S.item, justifyContent: "space-between", background: activeRunId === t.run_id ? `${T.accent}18` : "transparent" }} data-testid={`workspace-task-${t.run_id}`}>
                  <span onClick={() => onLoadTask(t.run_id)} style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {t.label || t.query || t.prompt || t.run_id}
                  </span>
                  <div className="ws-actions" style={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <button onClick={() => setMoveFor(moveFor === t.run_id ? null : t.run_id)} style={S.iconBtn} title="Move to project" data-testid={`workspace-move-${t.run_id}`}><FolderInput size={13} /></button>
                    <button onClick={() => { setEditing(t.run_id); setDraft(t.label || t.query || ""); }} style={S.iconBtn} title="Rename" data-testid={`workspace-rename-${t.run_id}`}><Pencil size={12} /></button>
                    <button onClick={() => deleteTask(t.run_id)} style={S.iconBtn} title="Delete" data-testid={`workspace-delete-${t.run_id}`}><Trash2 size={13} /></button>
                  </div>
                  {moveFor === t.run_id && (
                    <div style={{ position: "absolute", right: 6, top: "100%", zIndex: 40, background: T.panel, border: `1px solid ${T.border}`, borderRadius: 9, padding: 6, width: 200, boxShadow: "0 10px 30px rgba(0,0,0,0.4)" }} data-testid={`workspace-move-menu-${t.run_id}`}>
                      {projects.length === 0 ? <div style={{ fontSize: 12, color: T.textDim, padding: 4 }}>Create a project first.</div> :
                        projects.map((p) => (
                          <button key={p.id} onClick={() => moveToProject(t.run_id, t.label || t.query, p.id)} style={{ ...S.item, width: "100%", padding: "6px 8px", fontSize: 12.5 }} data-testid={`workspace-move-to-${p.id}`}>
                            <FolderKanban size={12} /> <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
          );
        })()}
      </div>

      <style>{`
        [data-testid="swarm-workspace"] .ws-actions { opacity: 0; transition: opacity 0.15s ease; }
        [data-testid="swarm-workspace"] .ws-row:hover .ws-actions { opacity: 1; }
        [data-testid="swarm-workspace"] .ws-row:hover { background: ${T.bg}; border-radius: 8px; }
      `}</style>

      {showBoard && createPortal((
        <div onClick={() => setShowBoard(false)} data-testid="workspace-leaderboard-modal"
          style={{ position: "fixed", inset: 0, zIndex: 2147483000, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 560, maxWidth: "94vw", maxHeight: "82vh", overflow: "hidden", display: "flex", flexDirection: "column", background: T.panel, border: `1px solid ${T.border}`, borderRadius: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: `1px solid ${T.border}` }}>
              <span style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 15, fontWeight: 800, color: T.textH }}><Trophy size={17} color={T.accent} /> {brand} — Project Value Leaderboard</span>
              <button onClick={() => setShowBoard(false)} style={S.iconBtn} data-testid="workspace-leaderboard-close"><X size={18} /></button>
            </div>
            <div style={{ overflowY: "auto", padding: 14 }}>
              {board === null ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.textDim, padding: 20, justifyContent: "center" }}><Loader2 size={16} className="animate-spin" /> Ranking projects…</div>
              ) : board.length === 0 ? (
                <div style={{ color: T.textDim, padding: 20, textAlign: "center", fontSize: 13 }}>No projects with runs yet. Ask questions inside a project to build the leaderboard.</div>
              ) : board.map((r, i) => (
                <div key={r.project_id} data-testid={`leaderboard-row-${r.project_id}`}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 11, marginBottom: 8, background: T.bg, border: `1px solid ${i === 0 ? `${T.accent}66` : T.border}` }}>
                  <div style={{ width: 26, textAlign: "center", fontSize: 16, fontWeight: 800, color: i === 0 ? T.accent : T.textDim }}>{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: T.textH, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
                      {r.shared ? <Users size={12} color={T.accent} title="Shared" /> : <Lock size={11} color={T.textDim} title="Private" />}
                    </div>
                    <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>{r.runs} run{r.runs !== 1 ? "s" : ""} · {r.completed} completed{r.owner_name && r.owner_name !== "—" ? ` · ${r.owner_name}` : ""}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 17, fontWeight: 800, color: T.accent }}>{fmtUsd(r.total_value_found)}</div>
                    <div style={{ fontSize: 9.5, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textDim }}>value found</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
